import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout';
import ApiClient from '../../utils/ApiClient';
import DescriptionWithTags from '../../components/DescriptionWithTags/DescriptionWithTags';
import { firstResolvedDisplayName } from '../../utils/DisplayValueUtils';
import { getFirstAttributeValue } from '../../utils/KeycloakAttributeUtils';
import { getUserTypeDisplayLabel, normalizeUserTypeCode } from '../../utils/UserTypeUtils';
import { formatRequestId } from '../../utils/RequestIdHelper';
import { formatStatusForDisplay } from '../../utils/StatusDisplayHelper';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import ProfileCompletionRadial from '../../components/ProfileCompletionRadial/ProfileCompletionRadial';

const normalizeIdentifier = (value) => String(value ?? '').trim();
const resolveGroupId = (group) => group?.id ?? group?.uuid ?? group?.name;
const resolveRoleId = (role) => role?.id ?? role?.uuid ?? role?.name;
const RESCIND_BLOCKED_ROLE_TAG = 'unlock';
const isRoleRescindBlocked = (role) => String(role?.clientName ?? '').trim() === RESCIND_BLOCKED_ROLE_TAG;
const CLIENT_LAST_LOGIN_ATTRIBUTE_PREFIX = 'lastLogin_';
const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
const RECENT_APP_WINDOW_DAYS = 30;
const normalizeRequestIdentifier = (value) => {
    if (!value) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value).trim();
    }
    if (typeof value === 'object') {
        return String(value.id ?? value.uuid ?? '').trim();
    }
    return '';
};
const normalizeRequestDisplayText = (value) => {
    if (!value) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value).trim();
    }
    if (typeof value === 'object') {
        return String(value.name ?? value.displayName ?? '').trim();
    }
    return '';
};
const normalizeRequestObjectDisplayText = (value) => {
    if (!value || typeof value !== 'object') {
        return '';
    }
    return String(value.name ?? value.displayName ?? '').trim();
};
const extractRequestList = (responseData) =>
    Array.isArray(responseData) ? responseData : responseData?.content ?? responseData?.requests ?? [];
const extractRequestSupervisorId = (request) =>
    normalizeRequestIdentifier(
        request?.supervisorUuid ??
            request?.supervisorId ??
            request?.supervisorUserId ??
            request?.supervisorUserUuid ??
            request?.securityManagerUuid ??
            request?.securityManagerId ??
            request?.supervisor
    );
const extractRequestEntitlementId = (request) =>
    normalizeRequestIdentifier(request?.groupUuid ?? request?.groupId ?? request?.group);
const extractRequestEntitlementOwnerGroupId = (request) =>
    normalizeRequestIdentifier(
        request?.entitlementOwnerUuid ??
            request?.entitlementOwnerId ??
            request?.entitlementOwner ??
            request?.entitlementOwnerGroupUuid ??
            request?.entitlementOwnerGroupId ??
            (Array.isArray(request?.entitlementOwners) ? request.entitlementOwners[0] : null) ??
            request?.ownerUuid ??
            request?.ownerId ??
            request?.owner
    );
const resolveRequestUserName = (user, fallbackLabel = 'Unknown user') =>
    firstResolvedDisplayName(
        [
            user?.name,
            user?.displayName,
            `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            user?.preferred_username,
            user?.username,
            user?.email,
        ],
        fallbackLabel
    );
const resolveRequestGroupName = (group, fallbackLabel = 'Unknown group') =>
    firstResolvedDisplayName([group?.name, group?.displayName], fallbackLabel);
const normalizeRequestRoleName = (role) => {
    if (!role) {
        return '';
    }
    if (typeof role === 'string' || typeof role === 'number') {
        return String(role).trim();
    }
    if (typeof role === 'object') {
        return String(role.name ?? role.displayName ?? role.id ?? role.uuid ?? '').trim();
    }
    return '';
};
const extractRequestRoleNames = (request) => {
    const roles = Array.isArray(request?.roles) ? request.roles : [];
    const normalizedRoles = roles.map((role) => normalizeRequestRoleName(role)).filter(Boolean);
    if (normalizedRoles.length > 0) {
        return normalizedRoles;
    }
    const singleRole = normalizeRequestRoleName(request?.role ?? request?.roleName);
    return singleRole ? [singleRole] : [];
};
const REQUEST_FILTER_OPTIONS = {
    OPEN: 'open',
    CLOSED: 'closed',
    ALL: 'all',
};
const CLOSED_REQUEST_STATUSES = new Set([
    'APPROVED',
    'CLOSED',
    'COMPLETED',
    'CANCELLED',
    'DENIED',
    'FULFILLED',
    'IMPLEMENTED',
    'REJECTED',
    'RESOLVED',
    'WITHDRAWN',
]);
const isClosedRequest = (request) => {
    if (typeof request?.closed === 'boolean') {
        return request.closed;
    }
    const status = String(request?.state ?? request?.status ?? '')
        .trim()
        .toUpperCase();
    if (!status) {
        return false;
    }
    if (CLOSED_REQUEST_STATUSES.has(status)) {
        return true;
    }
    return status.includes('CLOSED') || status.includes('COMPLETE');
};

const extractDirectReports = (responseData) => {
    if (Array.isArray(responseData)) {
        return responseData;
    }
    return responseData?.content ?? responseData?.users ?? responseData?.reports ?? [];
};

const resolveDirectReportIdentifiers = (report) => {
    if (!report || typeof report !== 'object') {
        return [];
    }

    const candidates = [
        report.uuid,
        report.id,
        report.userId,
        report.user_id,
        report.username,
        report.email,
    ];

    return Array.from(new Set(candidates.map((value) => normalizeIdentifier(value)).filter(Boolean)));
};

const formatLastUpdatedForLocalDisplay = (value) => {
    if (!value) {
        return 'Never';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return value;
    }

    const resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    const formatter = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    });
    const formattedValue = formatter.format(parsedDate);

    return resolvedTimeZone ? `${formattedValue} (${resolvedTimeZone})` : formattedValue;
};

const calculateDaysSinceTimestamp = (value, now = Date.now()) => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
        return null;
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    return Math.max(0, Math.floor((now - parsedDate.getTime()) / MILLISECONDS_PER_DAY));
};

const formatClientLastLoginLabel = (attributeKey) => {
    const normalizedKey = String(attributeKey ?? '').trim();
    if (!normalizedKey.startsWith(CLIENT_LAST_LOGIN_ATTRIBUTE_PREFIX)) {
        return normalizedKey || 'Unknown app';
    }

    const rawClientId = normalizedKey.slice(CLIENT_LAST_LOGIN_ATTRIBUTE_PREFIX.length);
    const label = rawClientId
        .split('_')
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');

    return label || 'Unknown app';
};

const parseBooleanPreference = (value, fallback = false) => {
    if (typeof value === 'boolean') {
        return value;
    }

    const normalizedValue = String(value ?? '').trim().toLowerCase();
    if (normalizedValue === 'true') {
        return true;
    }
    if (normalizedValue === 'false') {
        return false;
    }

    return fallback;
};

const hasBooleanPreferenceValue = (value) => {
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    return normalizedValue === 'true' || normalizedValue === 'false';
};

const formatDateOnlyForDisplay = (value, fallback = 'Not set') => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
        return fallback;
    }

    const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
    const dateOnlyMatch = rawValue.match(dateOnlyPattern);
    if (dateOnlyMatch) {
        const year = Number(dateOnlyMatch[1]);
        const monthIndex = Number(dateOnlyMatch[2]) - 1;
        const day = Number(dateOnlyMatch[3]);
        const utcDate = new Date(Date.UTC(year, monthIndex, day));
        return new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
        }).format(utcDate);
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return rawValue;
    }

    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(parsedDate);
};

const SupervisedUserPage = ({ authDetails, onSignOut }) => {
    const { uuid: routeUuid } = useParams();
    const requestedUserId = useMemo(() => normalizeIdentifier(routeUuid), [routeUuid]);

    const supervisorId =
        authDetails?.identity?.tokenParsed?.sub ??
        authDetails?.identity?.tokenParsed?.preferred_username ??
        authDetails?.identity?.tokenParsed?.email ??
        authDetails?.identity?.subject ??
        '';
    const normalizedSupervisorId = normalizeIdentifier(supervisorId);

    const [isCheckingAuthorization, setIsCheckingAuthorization] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [authorizationError, setAuthorizationError] = useState('');

    const [profileDetails, setProfileDetails] = useState(null);
    const [profileError, setProfileError] = useState('');
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);

    const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
    const [isLoadingPhoto, setIsLoadingPhoto] = useState(true);

    const [supervisorDetails, setSupervisorDetails] = useState(null);
    const [isLoadingSupervisor, setIsLoadingSupervisor] = useState(true);
    const [groups, setGroups] = useState([]);
    const [groupsError, setGroupsError] = useState('');
    const [isLoadingGroups, setIsLoadingGroups] = useState(true);
    const [groupRescindSuccess, setGroupRescindSuccess] = useState('');
    const [groupRescindError, setGroupRescindError] = useState('');
    const [rescindingGroupId, setRescindingGroupId] = useState('');
    const [roles, setRoles] = useState([]);
    const [rolesError, setRolesError] = useState('');
    const [isLoadingRoles, setIsLoadingRoles] = useState(true);
    const [roleRescindSuccess, setRoleRescindSuccess] = useState('');
    const [roleRescindError, setRoleRescindError] = useState('');
    const [rescindingRoleId, setRescindingRoleId] = useState('');
    const [requests, setRequests] = useState([]);
    const [requestsError, setRequestsError] = useState('');
    const [isLoadingRequests, setIsLoadingRequests] = useState(true);
    const [isLoadingRequestLookups, setIsLoadingRequestLookups] = useState(false);
    const [requestGroupLookup, setRequestGroupLookup] = useState(new Map());
    const [requestUserLookup, setRequestUserLookup] = useState(new Map());
    const [requestFilter, setRequestFilter] = useState(REQUEST_FILTER_OPTIONS.OPEN);

    useEffect(() => {
        let isMounted = true;

        if (!requestedUserId) {
            setIsAuthorized(false);
            setAuthorizationError('The user identifier is missing from the route.');
            setIsCheckingAuthorization(false);
            return () => {
                isMounted = false;
            };
        }

        if (!normalizedSupervisorId) {
            setIsAuthorized(false);
            setAuthorizationError('Your session is missing a supervisor identifier.');
            setIsCheckingAuthorization(false);
            return () => {
                isMounted = false;
            };
        }

        const verifyAuthorization = async () => {
            setIsCheckingAuthorization(true);
            setAuthorizationError('');

            try {
                const response = await ApiClient.get(
                    `/users/${encodeURIComponent(normalizedSupervisorId)}/directreports`
                );

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const directReports = extractDirectReports(response?.data);
                const hasAssignment = Array.isArray(directReports)
                    ? directReports.some((report) =>
                        resolveDirectReportIdentifiers(report).includes(requestedUserId)
                    )
                    : false;

                if (!isMounted) {
                    return;
                }

                setIsAuthorized(hasAssignment);
                if (!hasAssignment) {
                    setAuthorizationError('You are not assigned as the supervisor for this user.');
                }
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                setIsAuthorized(false);
                setAuthorizationError('We could not verify your supervisor assignment for this user.');
            } finally {
                if (isMounted) {
                    setIsCheckingAuthorization(false);
                }
            }
        };

        verifyAuthorization();

        return () => {
            isMounted = false;
        };
    }, [normalizedSupervisorId, requestedUserId]);

    useEffect(() => {
        let isMounted = true;

        if (!isAuthorized || !requestedUserId) {
            setProfileDetails(null);
            setProfileError('');
            setIsLoadingProfile(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchProfile = async () => {
            setIsLoadingProfile(true);
            setProfileError('');
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(requestedUserId)}`);
                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setProfileDetails(response.data ?? null);
                }
            } catch (error) {
                if (isMounted) {
                    setProfileDetails(null);
                    setProfileError('We could not load this profile.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingProfile(false);
                }
            }
        };

        fetchProfile();

        return () => {
            isMounted = false;
        };
    }, [isAuthorized, requestedUserId]);

    useEffect(() => {
        let isMounted = true;

        if (!isAuthorized || !requestedUserId) {
            setRoles([]);
            setRolesError('');
            setRoleRescindSuccess('');
            setRoleRescindError('');
            setRescindingRoleId('');
            setIsLoadingRoles(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchRoles = async () => {
            setIsLoadingRoles(true);
            setRolesError('');
            setRoleRescindSuccess('');
            setRoleRescindError('');
            setRescindingRoleId('');

            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(requestedUserId)}/roles`);
                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const responseData = response.data ?? {};
                const realmRoles = Array.isArray(responseData.realmMappings)
                    ? responseData.realmMappings.map((role) => ({
                        ...role,
                        clientName: null,
                    }))
                    : [];
                const clientMappings = responseData.clientMappings ?? {};
                const clientRoles = Object.values(clientMappings).flatMap((client) =>
                    Array.isArray(client?.mappings)
                        ? client.mappings.map((role) => ({
                            ...role,
                            clientName: client.client ?? client.clientId ?? null,
                        }))
                        : []
                );

                if (isMounted) {
                    setRoles([...realmRoles, ...clientRoles]);
                }
            } catch (error) {
                if (isMounted) {
                    setRoles([]);
                    setRolesError('We could not load this user\'s roles.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingRoles(false);
                }
            }
        };

        fetchRoles();

        return () => {
            isMounted = false;
        };
    }, [isAuthorized, requestedUserId]);

    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;

        if (!isAuthorized || !requestedUserId) {
            setProfilePhotoUrl(null);
            setIsLoadingPhoto(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchProfilePhoto = async () => {
            setIsLoadingPhoto(true);
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(requestedUserId)}/picture`, {
                    responseType: 'blob',
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const contentType = response.headers?.['content-type'] ?? '';
                if (contentType && !contentType.startsWith('image/')) {
                    throw new Error(`Unexpected content type: ${contentType}`);
                }

                objectUrl = URL.createObjectURL(response.data);
                if (isMounted) {
                    setProfilePhotoUrl(objectUrl);
                }
            } catch (error) {
                if (isMounted) {
                    setProfilePhotoUrl(null);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingPhoto(false);
                }
            }
        };

        fetchProfilePhoto();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [isAuthorized, requestedUserId]);

    const profileAttributes =
        profileDetails?.attributes && typeof profileDetails.attributes === 'object'
            ? profileDetails.attributes
            : {};

    const supervisorLookupId = getFirstAttributeValue(profileAttributes, ['supervisor', 'Supervisor'], '');

    useEffect(() => {
        let isMounted = true;
        const normalizedSupervisorLookupId = normalizeIdentifier(supervisorLookupId);

        if (!isAuthorized || !normalizedSupervisorLookupId) {
            setSupervisorDetails(null);
            setIsLoadingSupervisor(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchSupervisor = async () => {
            setIsLoadingSupervisor(true);
            try {
                const response = await ApiClient.get(
                    `/users/${encodeURIComponent(normalizedSupervisorLookupId)}`
                );

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setSupervisorDetails(response.data ?? null);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorDetails(null);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingSupervisor(false);
                }
            }
        };

        fetchSupervisor();

        return () => {
            isMounted = false;
        };
    }, [isAuthorized, supervisorLookupId]);

    useEffect(() => {
        let isMounted = true;

        if (!isAuthorized || !requestedUserId) {
            setGroups([]);
            setGroupsError('');
            setGroupRescindSuccess('');
            setGroupRescindError('');
            setRescindingGroupId('');
            setIsLoadingGroups(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchGroups = async () => {
            setIsLoadingGroups(true);
            setGroupsError('');
            setGroupRescindSuccess('');
            setGroupRescindError('');
            setRescindingGroupId('');

            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(requestedUserId)}/groups`);
                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const responseData = response.data ?? {};
                const groupList = Array.isArray(responseData?.content) ? responseData.content : [];

                if (isMounted) {
                    setGroups(groupList);
                }
            } catch (error) {
                if (isMounted) {
                    setGroups([]);
                    setGroupsError('We could not load this user\'s groups.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingGroups(false);
                }
            }
        };

        fetchGroups();

        return () => {
            isMounted = false;
        };
    }, [isAuthorized, requestedUserId]);

    useEffect(() => {
        let isMounted = true;

        if (!isAuthorized || !requestedUserId) {
            setRequests([]);
            setRequestsError('');
            setIsLoadingRequests(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchRequests = async () => {
            setIsLoadingRequests(true);
            setRequestsError('');

            try {
                const response = await ApiClient.get('/access-requests', {
                    params: {
                        page: 0,
                        size: 100,
                        requester: requestedUserId,
                    },
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    const responseData = response.data ?? {};
                    const requestList = extractRequestList(responseData);
                    const normalizedRequestList = Array.isArray(requestList) ? requestList : [];
                    setIsLoadingRequestLookups(normalizedRequestList.length > 0);
                    setRequests(normalizedRequestList);
                }
            } catch (error) {
                if (isMounted) {
                    setRequests([]);
                    setRequestsError('We could not load this user\'s access requests.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingRequests(false);
                }
            }
        };

        fetchRequests();

        return () => {
            isMounted = false;
        };
    }, [isAuthorized, requestedUserId]);

    useEffect(() => {
        let isMounted = true;
        const groupIds = new Set();
        const userIds = new Set();

        requests.forEach((request) => {
            const entitlementId = extractRequestEntitlementId(request);
            if (entitlementId) {
                groupIds.add(entitlementId);
            }

            const ownerGroupId = extractRequestEntitlementOwnerGroupId(request);
            if (ownerGroupId) {
                groupIds.add(ownerGroupId);
            }

            const supervisorIdForRequest = extractRequestSupervisorId(request);
            if (supervisorIdForRequest) {
                userIds.add(supervisorIdForRequest);
            }
        });

        if (groupIds.size === 0) {
            setRequestGroupLookup(new Map());
        }

        if (userIds.size === 0) {
            setRequestUserLookup(new Map());
        }

        if (groupIds.size === 0 && userIds.size === 0) {
            setIsLoadingRequestLookups(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchRequestLookups = async () => {
            setIsLoadingRequestLookups(true);
            try {
                const [groupsFromApi, usersFromApi] = await Promise.all([
                    Promise.all(
                        Array.from(groupIds).map((groupId) =>
                            ApiClient.get(`/groups/${encodeURIComponent(groupId)}`)
                                .then((response) => ({
                                    id: groupId,
                                    name: resolveRequestGroupName(response?.data, groupId),
                                }))
                                .catch(() => null)
                        )
                    ),
                    Promise.all(
                        Array.from(userIds).map((userId) =>
                            ApiClient.get(`/users/${encodeURIComponent(userId)}`)
                                .then((response) => ({
                                    id: userId,
                                    name: resolveRequestUserName(response?.data, userId),
                                }))
                                .catch(() => null)
                        )
                    ),
                ]);

                if (!isMounted) {
                    return;
                }

                const nextGroupLookup = new Map();
                groupsFromApi.filter(Boolean).forEach((group) => {
                    if (group.id && group.name) {
                        nextGroupLookup.set(group.id, group.name);
                    }
                });
                setRequestGroupLookup(nextGroupLookup);

                const nextUserLookup = new Map();
                usersFromApi.filter(Boolean).forEach((user) => {
                    if (user.id && user.name) {
                        nextUserLookup.set(user.id, user.name);
                    }
                });
                setRequestUserLookup(nextUserLookup);
            } catch (error) {
                if (isMounted) {
                    setRequestGroupLookup(new Map());
                    setRequestUserLookup(new Map());
                }
            } finally {
                if (isMounted) {
                    setIsLoadingRequestLookups(false);
                }
            }
        };

        fetchRequestLookups();

        return () => {
            isMounted = false;
        };
    }, [requests]);

    const handleRescindGroup = async (group) => {
        const groupId = normalizeIdentifier(resolveGroupId(group));
        const groupName = group?.name || groupId || 'Unknown group';

        if (!requestedUserId) {
            setGroupRescindError('We could not verify the user identity for this request.');
            return;
        }

        if (!groupId) {
            setGroupRescindError(`We could not resolve a group identifier for: ${groupName}`);
            return;
        }

        setRescindingGroupId(groupId);
        setGroupRescindSuccess('');
        setGroupRescindError('');

        try {
            const response = await ApiClient.post(
                `/user/${encodeURIComponent(requestedUserId)}/group/${encodeURIComponent(groupId)}/rescind`
            );

            if (!response?.status || response.status < 200 || response.status >= 300) {
                throw new Error(`API responded with ${response?.status ?? 'unknown status'}`);
            }

            setGroups((previous) => previous.filter((existingGroup) => resolveGroupId(existingGroup) !== groupId));
            setGroupRescindSuccess(`Rescinded access for: ${groupName}`);
        } catch (error) {
            setGroupRescindError(`We could not rescind access for: ${groupName}`);
        } finally {
            setRescindingGroupId('');
        }
    };

    const handleRescindRole = async (role) => {
        const roleId = normalizeIdentifier(resolveRoleId(role));
        const roleName = role?.name || roleId || 'Unknown role';

        if (isRoleRescindBlocked(role)) {
            setRoleRescindSuccess('');
            setRoleRescindError(`Roles tagged ${RESCIND_BLOCKED_ROLE_TAG} cannot be rescinded: ${roleName}`);
            return;
        }

        if (!requestedUserId) {
            setRoleRescindError('We could not verify the user identity for this request.');
            return;
        }

        if (!roleId) {
            setRoleRescindError(`We could not resolve a role identifier for: ${roleName}`);
            return;
        }

        setRescindingRoleId(roleId);
        setRoleRescindSuccess('');
        setRoleRescindError('');

        try {
            const response = await ApiClient.post(
                `/user/${encodeURIComponent(requestedUserId)}/role/${encodeURIComponent(roleId)}/rescind`
            );

            if (!response?.status || response.status < 200 || response.status >= 300) {
                throw new Error(`API responded with ${response?.status ?? 'unknown status'}`);
            }

            setRoles((previous) => previous.filter((existingRole) => resolveRoleId(existingRole) !== roleId));
            setRoleRescindSuccess(`Rescinded roles: ${roleName}`);
        } catch (error) {
            setRoleRescindError(`Could not rescind roles: ${roleName}`);
        } finally {
            setRescindingRoleId('');
        }
    };

    const userTypeCode = normalizeUserTypeCode(
        getFirstAttributeValue(
            profileAttributes,
            ['UserType', 'userType'],
            profileDetails?.UserType ?? profileDetails?.userType ?? profileDetails?.user_type ?? ''
        )
    );
    const isContractor = userTypeCode === 'CTR';
    const contractNumberValue = getFirstAttributeValue(profileAttributes, ['contractNumber', 'ContractNumber'], '');
    const contractExpirationDateValue = getFirstAttributeValue(
        profileAttributes,
        ['contractExpirationDate', 'ContractExpirationDate'],
        ''
    );
    const slackNotificationsValue = getFirstAttributeValue(profileAttributes, ['slackNotifications'], '');
    const slackNotificationsEnabled = parseBooleanPreference(slackNotificationsValue, false);
    const emailNotificationsValue = getFirstAttributeValue(profileAttributes, ['emailNotifications'], '');
    const emailNotificationsEnabled = parseBooleanPreference(emailNotificationsValue, false);
    const lastUpdatedValue = getFirstAttributeValue(profileAttributes, ['lastUpdated', 'LastUpdated'], '');
    const formattedLastUpdated = formatLastUpdatedForLocalDisplay(lastUpdatedValue || 'Never');
    const lastLoginValue = getFirstAttributeValue(profileAttributes, ['last-login'], '');
    const daysSinceLastLogin = calculateDaysSinceTimestamp(lastLoginValue);
    const lastLoginDisplayValue =
        isLoadingProfile ? '...' : daysSinceLastLogin === null ? '--' : String(daysSinceLastLogin);
    const lastLoginSummary = isLoadingProfile
        ? 'Calculating last login activity...'
        : daysSinceLastLogin === null
          ? 'No last login recorded.'
          : `${daysSinceLastLogin === 1 ? 'day' : 'days'} since last login.`;
    const recentAppSessions = Object.keys(profileAttributes)
        .filter((attributeKey) => attributeKey.startsWith(CLIENT_LAST_LOGIN_ATTRIBUTE_PREFIX))
        .map((attributeKey) => {
            const timestamp = getFirstAttributeValue(profileAttributes, attributeKey, '');
            const parsedTimestamp = new Date(timestamp);
            return {
                attributeKey,
                label: formatClientLastLoginLabel(attributeKey),
                timestamp,
                parsedTimestamp,
                daysSince: calculateDaysSinceTimestamp(timestamp),
            };
        })
        .filter(
            (appSession) =>
                appSession.daysSince !== null &&
                appSession.daysSince < RECENT_APP_WINDOW_DAYS &&
                !Number.isNaN(appSession.parsedTimestamp.getTime())
        )
        .sort((left, right) => right.parsedTimestamp.getTime() - left.parsedTimestamp.getTime());

    const profileDisplayName = firstResolvedDisplayName(
        [
            profileDetails?.name,
            `${profileDetails?.firstName || ''} ${profileDetails?.lastName || ''}`.trim(),
            profileDetails?.preferred_username,
            profileDetails?.username,
            profileDetails?.email,
        ],
        'Unknown user'
    );
    const supervisorDisplayName = firstResolvedDisplayName(
        [
            supervisorDetails?.name,
            `${supervisorDetails?.firstName || ''} ${supervisorDetails?.lastName || ''}`.trim(),
            supervisorDetails?.preferred_username,
            supervisorDetails?.username,
            supervisorDetails?.email,
        ],
        'Unassigned'
    );

    const profile = {
        firstName: profileDetails?.firstName || 'Not set',
        lastName: profileDetails?.lastName || 'Not set',
        username: profileDetails?.username || profileDetails?.preferred_username || 'Not set',
        email: profileDetails?.email || 'Not set',
        jobTitle: getFirstAttributeValue(profileAttributes, ['jobTitle', 'JobTitle'], '') || 'Not set',
        userType: getUserTypeDisplayLabel(userTypeCode, 'Not set'),
        contractNumber: contractNumberValue || 'Not set',
        contractExpirationDate: formatDateOnlyForDisplay(contractExpirationDateValue),
        slackNotifications: slackNotificationsEnabled ? 'On' : 'Off',
        emailNotifications: emailNotificationsEnabled ? 'On' : 'Off',
        supervisor: supervisorDisplayName,
        lastUpdated: formattedLastUpdated,
    };

    const completionChecks = [
        Boolean(String(profileDetails?.firstName || '').trim()),
        Boolean(String(profileDetails?.lastName || '').trim()),
        Boolean(String(profileDetails?.username || profileDetails?.preferred_username || '').trim()),
        Boolean(String(profileDetails?.email || '').trim()),
        Boolean(String(supervisorLookupId || '').trim()),
        hasBooleanPreferenceValue(slackNotificationsValue),
        hasBooleanPreferenceValue(emailNotificationsValue),
        Boolean(profilePhotoUrl),
        Boolean(String(lastUpdatedValue || '').trim()),
        Boolean(String(getFirstAttributeValue(profileAttributes, ['jobTitle', 'JobTitle'], '')).trim()),
        Boolean(userTypeCode),
    ];
    if (isContractor) {
        completionChecks.push(Boolean(String(contractNumberValue || '').trim()));
        completionChecks.push(Boolean(String(contractExpirationDateValue || '').trim()));
    }

    const completedFieldsCount = completionChecks.filter(Boolean).length;
    const profileCompletionPercent =
        completionChecks.length > 0
            ? Math.round((completedFieldsCount / completionChecks.length) * 100)
            : 0;
    const slackNotificationsToggleId = 'supervised-profile-slack-notifications-readonly';
    const emailNotificationsToggleId = 'supervised-profile-email-notifications-readonly';
    const getRequestId = (request) =>
        request?.id ?? request?.requestId ?? request?.uuid ?? request?.accessRequestId ?? 'Unknown';
    const getDisplayRequestId = (request) => formatRequestId(getRequestId(request));
    const getRequestEntitlementName = (request) => {
        const explicitName =
            normalizeRequestDisplayText(request?.entitlement) ||
            normalizeRequestDisplayText(request?.entitlementName) ||
            normalizeRequestDisplayText(request?.groupName) ||
            normalizeRequestDisplayText(request?.roleName);
        if (explicitName) {
            return explicitName;
        }

        const entitlementId = extractRequestEntitlementId(request);
        if (entitlementId) {
            return requestGroupLookup.get(entitlementId) ?? 'Unknown entitlement';
        }

        const roleNames = extractRequestRoleNames(request);
        if (roleNames.length > 0) {
            return roleNames.join(', ');
        }

        return 'Unknown entitlement';
    };
    const getRequestEntitlementType = (request) =>
        request?.type ??
        request?.entitlementType ??
        (request?.group || request?.groupName || request?.groupUuid || request?.groupId
            ? 'Group'
            : request?.role || request?.roleName || request?.roles
              ? 'Role'
              : 'Request');
    const getRequestSupervisorName = (request) => {
        const explicitName =
            normalizeRequestDisplayText(request?.supervisorName) ||
            normalizeRequestDisplayText(request?.securityManagerName) ||
            normalizeRequestObjectDisplayText(request?.supervisor);
        if (explicitName) {
            return explicitName;
        }

        const supervisorIdForRequest = extractRequestSupervisorId(request);
        if (supervisorIdForRequest) {
            return requestUserLookup.get(supervisorIdForRequest) ?? 'Unknown user';
        }

        return 'Unassigned';
    };
    const getRequestOwnerName = (request) => {
        const explicitName =
            normalizeRequestDisplayText(request?.entitlementOwnerName) ||
            normalizeRequestObjectDisplayText(request?.entitlementOwner) ||
            normalizeRequestObjectDisplayText(request?.owner);
        if (explicitName) {
            return explicitName;
        }

        const ownerGroupId = extractRequestEntitlementOwnerGroupId(request);
        if (ownerGroupId) {
            return requestGroupLookup.get(ownerGroupId) ?? 'Unknown group';
        }

        return 'Unassigned';
    };
    const getRequestStatusLabel = (request) =>
        formatStatusForDisplay(request?.state ?? request?.status, 'Pending');
    const requestCounts = useMemo(() => {
        const closed = requests.filter((request) => isClosedRequest(request)).length;
        const open = requests.length - closed;
        return {
            open,
            closed,
            all: requests.length,
        };
    }, [requests]);
    const filteredRequests = useMemo(() => {
        if (requestFilter === REQUEST_FILTER_OPTIONS.OPEN) {
            return requests.filter((request) => !isClosedRequest(request));
        }
        if (requestFilter === REQUEST_FILTER_OPTIONS.CLOSED) {
            return requests.filter((request) => isClosedRequest(request));
        }
        return requests;
    }, [requestFilter, requests]);
    const emptyRequestFilterMessage =
        requestFilter === REQUEST_FILTER_OPTIONS.OPEN
            ? 'No open access requests found.'
            : requestFilter === REQUEST_FILTER_OPTIONS.CLOSED
              ? 'No closed access requests found.'
              : 'No access requests have been submitted yet.';

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section padding-bottom-0">
                <div className="grid-container">
                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h1 className="usa-card__heading">Direct Report Profile</h1>
                                    </div>
                                    <div className="usa-card__body">
                                        {isCheckingAuthorization ? (
                                            <p aria-hidden="true">
                                                <ValueWithSkeleton loading type="sentence" width="20ch" />
                                            </p>
                                        ) : authorizationError ? (
                                            <div className="usa-alert usa-alert--error" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{authorizationError}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="usa-alert usa-alert--warning" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">
                                                        This page is restricted to the assigned supervisor.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="usa-card__footer">
                                        <Link className="usa-button usa-button--outline" to="/approvals">
                                            Back to approvals
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {isAuthorized ? (
                <>
                    <section className="usa-section">
                        <div className="grid-container">
                            <div className="grid-row grid-gap">
                                <div className="tablet:grid-col-8">
                                    <div className="usa-card">
                                        <div className="usa-card__container">
                                            <div className="usa-card__header profile-card__header">
                                                <h2 className="usa-card__heading">
                                                    Profile information for: {profileDisplayName}
                                                </h2>
                                            </div>
                                            <div className="usa-card__body">
                                                {profileError ? (
                                                    <div className="usa-alert usa-alert--error" role="alert">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{profileError}</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="approval-requester profile-requester">
                                                        <div
                                                            className={
                                                                profilePhotoUrl
                                                                    ? 'approval-requester__avatar approval-requester__avatar--photo'
                                                                    : 'approval-requester__avatar'
                                                            }
                                                            aria-hidden={!profilePhotoUrl}
                                                        >
                                                            {profilePhotoUrl && !isLoadingPhoto ? (
                                                                <img
                                                                    className="approval-requester__avatar-image"
                                                                    src={profilePhotoUrl}
                                                                    alt={profileDisplayName}
                                                                />
                                                            ) : null}
                                                        </div>
                                                        <div className="approval-requester__info">
                                                            <p>
                                                                <span className="approval-key">First name</span>
                                                                <ValueWithSkeleton
                                                                    value={profile.firstName}
                                                                    loading={isLoadingProfile}
                                                                    fallback="Not set"
                                                                    width="12ch"
                                                                />
                                                            </p>
                                                            <p>
                                                                <span className="approval-key">Last name</span>
                                                                <ValueWithSkeleton
                                                                    value={profile.lastName}
                                                                    loading={isLoadingProfile}
                                                                    fallback="Not set"
                                                                    width="12ch"
                                                                />
                                                            </p>
                                                            <p>
                                                                <span className="approval-key">Username</span>
                                                                <ValueWithSkeleton
                                                                    value={profile.username}
                                                                    loading={isLoadingProfile}
                                                                    fallback="Not set"
                                                                    width="14ch"
                                                                />
                                                            </p>
                                                            <p>
                                                                <span className="approval-key">Email</span>
                                                                <ValueWithSkeleton
                                                                    value={profile.email}
                                                                    loading={isLoadingProfile}
                                                                    fallback="Not set"
                                                                    width="16ch"
                                                                />
                                                            </p>
                                                            <p>
                                                                <span className="approval-key">Job title</span>
                                                                <ValueWithSkeleton
                                                                    value={profile.jobTitle}
                                                                    loading={isLoadingProfile}
                                                                    fallback="Not set"
                                                                    width="14ch"
                                                                />
                                                            </p>
                                                            <p>
                                                                <span className="approval-key">User type</span>
                                                                <ValueWithSkeleton
                                                                    value={profile.userType}
                                                                    loading={isLoadingProfile}
                                                                    fallback="Not set"
                                                                    width="10ch"
                                                                />
                                                            </p>
                                                            {isContractor ? (
                                                                <>
                                                                    <p>
                                                                        <span className="approval-key">Contract number</span>
                                                                        <ValueWithSkeleton
                                                                            value={profile.contractNumber}
                                                                            loading={isLoadingProfile}
                                                                            fallback="Not set"
                                                                            width="12ch"
                                                                        />
                                                                    </p>
                                                                    <p>
                                                                        <span className="approval-key">Contract expiration date</span>
                                                                        <ValueWithSkeleton
                                                                            value={profile.contractExpirationDate}
                                                                            loading={isLoadingProfile}
                                                                            fallback="Not set"
                                                                            width="12ch"
                                                                        />
                                                                    </p>
                                                                </>
                                                            ) : null}
                                                            <p className="profile-card__slack-row">
                                                                <span className="approval-key">Slack notifications</span>
                                                                {isLoadingProfile ? (
                                                                    <ValueWithSkeleton loading type="sentence" width="6ch" />
                                                                ) : (
                                                                    <span className="usa-toggle profile-card__toggle">
                                                                        <input
                                                                            id={slackNotificationsToggleId}
                                                                            className="usa-toggle__input"
                                                                            type="checkbox"
                                                                            checked={slackNotificationsEnabled}
                                                                            aria-label={`Slack notifications: ${profile.slackNotifications}`}
                                                                            readOnly
                                                                            disabled
                                                                        />
                                                                        <label
                                                                            className="usa-toggle__label"
                                                                            htmlFor={slackNotificationsToggleId}
                                                                        >
                                                                            <span className="profile-card__toggle-state">
                                                                                {profile.slackNotifications}
                                                                            </span>
                                                                        </label>
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="profile-card__slack-row">
                                                                <span className="approval-key">Email notifications</span>
                                                                {isLoadingProfile ? (
                                                                    <ValueWithSkeleton loading type="sentence" width="6ch" />
                                                                ) : (
                                                                    <span className="usa-toggle profile-card__toggle">
                                                                        <input
                                                                            id={emailNotificationsToggleId}
                                                                            className="usa-toggle__input"
                                                                            type="checkbox"
                                                                            checked={emailNotificationsEnabled}
                                                                            aria-label={`Email notifications: ${profile.emailNotifications}`}
                                                                            readOnly
                                                                            disabled
                                                                        />
                                                                        <label
                                                                            className="usa-toggle__label"
                                                                            htmlFor={emailNotificationsToggleId}
                                                                        >
                                                                            <span className="profile-card__toggle-state">
                                                                                {profile.emailNotifications}
                                                                            </span>
                                                                        </label>
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p>
                                                                <span className="approval-key">Supervisor</span>
                                                                <ValueWithSkeleton
                                                                    value={profile.supervisor}
                                                                    loading={isLoadingSupervisor}
                                                                    fallback="Unassigned"
                                                                    width="13ch"
                                                                />
                                                            </p>
                                                            <p className="profile-card__updated">
                                                                <span className="approval-key">Last updated</span>
                                                                <ValueWithSkeleton
                                                                    value={profile.lastUpdated}
                                                                    loading={isLoadingProfile}
                                                                    fallback="Never"
                                                                    width="18ch"
                                                                />
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="tablet:grid-col-4">
                                    <div className="profile-sidebar-cards">
                                        <div className="usa-card profile-completion-card">
                                            <div className="usa-card__container">
                                                <div className="usa-card__header">
                                                    <h2 className="usa-card__heading">Profile completion</h2>
                                                </div>
                                                <div className="usa-card__body profile-completion-card__body">
                                                    <ProfileCompletionRadial percent={profileCompletionPercent} />
                                                    <p className="profile-completion-card__summary">
                                                        {completedFieldsCount} of {completionChecks.length} profile fields complete
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="usa-card">
                                            <div className="usa-card__container">
                                                <div className="usa-card__header">
                                                    <h2 className="usa-card__heading">Last login</h2>
                                                </div>
                                                <div className="usa-card__body profile-sidebar-stat-card__body">
                                                    <div className="usa-prose">
                                                        <div className="usa-display">
                                                            {isLoadingProfile ? (
                                                                <ValueWithSkeleton loading type="heading" width="3ch" />
                                                            ) : (
                                                                lastLoginDisplayValue
                                                            )}
                                                        </div>
                                                        <p className="profile-sidebar-stat-card__summary">
                                                            {isLoadingProfile ? (
                                                                <ValueWithSkeleton loading type="sentence" width="22ch" />
                                                            ) : (
                                                                lastLoginSummary
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="usa-card">
                                            <div className="usa-card__container">
                                                <div className="usa-card__header">
                                                    <h2 className="usa-card__heading">
                                                        Recent Apps
                                                    </h2>
                                                </div>
                                                <div className="usa-card__body profile-sidebar-tag-card__body">
                                                    {isLoadingProfile ? (
                                                        <div className="profile-sidebar-tag-list" aria-hidden="true">
                                                            {Array.from({ length: 3 }).map((_, index) => (
                                                                <span key={`recent-app-loading-${index}`} className="usa-tag">
                                                                    <ValueWithSkeleton loading type="chip" width="7ch" />
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : recentAppSessions.length > 0 ? (
                                                        <div className="profile-sidebar-tag-list">
                                                            {recentAppSessions.map((appSession) => (
                                                                <span
                                                                    key={appSession.attributeKey}
                                                                    className="usa-tag"
                                                                >
                                                                    {appSession.label}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="profile-sidebar-empty-state">
                                                            No client sessions recorded in the last 30 days.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section className="usa-section padding-top-0">
                        <div className="grid-container">
                            <div className="grid-row grid-gap">
                                <div className="tablet:grid-col-12">
                                    <div className="usa-card">
                                        <div className="usa-card__container">
                                            <div className="usa-card__header">
                                                <h2 className="usa-card__heading">Groups</h2>
                                            </div>
                                            <div className="usa-card__body">
                                                {groupRescindSuccess ? (
                                                    <div className="usa-alert usa-alert--success margin-bottom-2" role="status">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{groupRescindSuccess}</p>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {groupRescindError ? (
                                                    <div className="usa-alert usa-alert--error margin-bottom-2" role="alert">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{groupRescindError}</p>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {isLoadingGroups ? (
                                                    <div className="usa-form" aria-hidden="true">
                                                        <fieldset className="usa-fieldset">
                                                            {Array.from({ length: 3 }).map((_, index) => (
                                                                <div key={`supervised-group-loading-${index}`} className="usa-checkbox">
                                                                    <input
                                                                        className="usa-checkbox__input usa-checkbox__input--tile"
                                                                        type="checkbox"
                                                                        disabled
                                                                    />
                                                                    <label className="usa-checkbox__label">
                                                                        <ValueWithSkeleton loading type="sentence" width="16ch" />
                                                                        <span className="usa-checkbox__label-description">
                                                                            <ValueWithSkeleton loading type="sentence" width="26ch" />
                                                                        </span>
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </fieldset>
                                                    </div>
                                                ) : groupsError ? (
                                                    <div className="usa-alert usa-alert--error" role="alert">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{groupsError}</p>
                                                        </div>
                                                    </div>
                                                ) : groups.length === 0 ? (
                                                    <p>No access groups are currently assigned to this account.</p>
                                                ) : (
                                                    <div className="usa-form">
                                                        <fieldset className="usa-fieldset">
                                                            {groups.map((group, index) => {
                                                                const groupId = normalizeIdentifier(resolveGroupId(group));
                                                                const idSuffix = groupId || `group-${index + 1}`;
                                                                const isRescindingThisGroup = rescindingGroupId === groupId;
                                                                return (
                                                                    <div key={idSuffix} className="usa-checkbox supervised-access-action">
                                                                        <input
                                                                            className="usa-checkbox__input usa-checkbox__input--tile"
                                                                            id={`supervised-group-${idSuffix}`}
                                                                            type="checkbox"
                                                                            name="supervised-access-groups"
                                                                            checked
                                                                            readOnly
                                                                            disabled
                                                                        />
                                                                        <label
                                                                            className="usa-checkbox__label"
                                                                            htmlFor={`supervised-group-${idSuffix}`}
                                                                        >
                                                                            <span className="supervised-access-action__details">
                                                                                {group?.name || groupId || 'Unknown group'}
                                                                                <span className="usa-checkbox__label-description">
                                                                                    <DescriptionWithTags text={group?.description} />
                                                                                </span>
                                                                            </span>
                                                                        </label>
                                                                        <button
                                                                            type="button"
                                                                            className="usa-button usa-button--secondary supervised-access-action__button"
                                                                            onClick={() => handleRescindGroup(group)}
                                                                            disabled={Boolean(rescindingGroupId)}
                                                                            aria-label={`Rescind ${group?.name || groupId || 'group access'}`}
                                                                        >
                                                                            {isRescindingThisGroup ? 'Rescinding...' : 'Rescind'}
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </fieldset>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section className="usa-section padding-top-0">
                        <div className="grid-container">
                            <div className="grid-row grid-gap">
                                <div className="tablet:grid-col-12">
                                    <div className="usa-card">
                                        <div className="usa-card__container">
                                            <div className="usa-card__header">
                                                <h2 className="usa-card__heading">Roles</h2>
                                            </div>
                                            <div className="usa-card__body">
                                                {roleRescindSuccess ? (
                                                    <div className="usa-alert usa-alert--success margin-bottom-2" role="status">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{roleRescindSuccess}</p>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {roleRescindError ? (
                                                    <div className="usa-alert usa-alert--error margin-bottom-2" role="alert">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{roleRescindError}</p>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {isLoadingRoles ? (
                                                    <div className="usa-form" aria-hidden="true">
                                                        <fieldset className="usa-fieldset">
                                                            {Array.from({ length: 3 }).map((_, index) => (
                                                                <div key={`supervised-role-loading-${index}`} className="usa-checkbox">
                                                                    <input
                                                                        className="usa-checkbox__input usa-checkbox__input--tile"
                                                                        type="checkbox"
                                                                        disabled
                                                                    />
                                                                    <label className="usa-checkbox__label">
                                                                        <ValueWithSkeleton loading type="chip" width="6ch" />{' '}
                                                                        <ValueWithSkeleton loading type="sentence" width="18ch" />
                                                                        <span className="usa-checkbox__label-description">
                                                                            <ValueWithSkeleton loading type="sentence" width="28ch" />
                                                                        </span>
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </fieldset>
                                                    </div>
                                                ) : rolesError ? (
                                                    <div className="usa-alert usa-alert--error" role="alert">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{rolesError}</p>
                                                        </div>
                                                    </div>
                                                ) : roles.length === 0 ? (
                                                    <p>No roles are currently assigned to this account.</p>
                                                ) : (
                                                    <div className="usa-form">
                                                        <fieldset className="usa-fieldset">
                                                            {roles.map((role, index) => {
                                                                const roleId = normalizeIdentifier(resolveRoleId(role));
                                                                const idSuffix = roleId || `role-${index + 1}`;
                                                                const isRescindBlocked = isRoleRescindBlocked(role);
                                                                const isRescindingThisRole = rescindingRoleId === roleId;
                                                                return (
                                                                    <div key={idSuffix} className="usa-checkbox supervised-access-action">
                                                                        <input
                                                                            className="usa-checkbox__input usa-checkbox__input--tile"
                                                                            id={`supervised-role-${idSuffix}`}
                                                                            type="checkbox"
                                                                            name="supervised-access-roles"
                                                                            checked
                                                                            readOnly
                                                                            disabled
                                                                        />
                                                                        <label
                                                                            className="usa-checkbox__label"
                                                                            htmlFor={`supervised-role-${idSuffix}`}
                                                                        >
                                                                            <span className="supervised-access-action__details">
                                                                                {role?.clientName ? (
                                                                                    <span className="usa-tag usa-tag--cool">
                                                                                        {role.clientName}
                                                                                    </span>
                                                                                ) : null}
                                                                                {role?.name || roleId || 'Unknown role'}
                                                                                <span className="usa-checkbox__label-description">
                                                                                    {role?.description || 'No description provided.'}
                                                                                </span>
                                                                                {isRescindBlocked ? (
                                                                                    <span className="usa-checkbox__label-description">
                                                                                        Roles tagged {RESCIND_BLOCKED_ROLE_TAG} cannot be rescinded.
                                                                                    </span>
                                                                                ) : null}
                                                                            </span>
                                                                        </label>
                                                                        <button
                                                                            type="button"
                                                                            className="usa-button usa-button--secondary supervised-access-action__button"
                                                                            onClick={() => handleRescindRole(role)}
                                                                            disabled={Boolean(rescindingRoleId) || isRescindBlocked}
                                                                            aria-label={`Rescind ${role?.name || roleId || 'role access'}`}
                                                                        >
                                                                            {isRescindingThisRole ? 'Rescinding...' : 'Rescind'}
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </fieldset>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section className="usa-section padding-top-0">
                        <div className="grid-container">
                            <div className="grid-row grid-gap">
                                <div className="tablet:grid-col-12">
                                    <div className="usa-card">
                                        <div className="usa-card__container">
                                            <div className="usa-card__header">
                                                <h2 className="usa-card__heading">Requests</h2>
                                            </div>
                                            <div className="usa-card__body">
                                                {isLoadingRequests ? (
                                                    <div className="usa-form" aria-hidden="true">
                                                        <fieldset className="usa-fieldset">
                                                            {Array.from({ length: 3 }).map((_, index) => (
                                                                <div key={`supervised-request-loading-${index}`} className="usa-checkbox">
                                                                    <input
                                                                        className="usa-checkbox__input usa-checkbox__input--tile"
                                                                        type="checkbox"
                                                                        disabled
                                                                    />
                                                                    <label className="usa-checkbox__label">
                                                                        <ValueWithSkeleton loading width="9ch" />
                                                                        <span className="usa-checkbox__label-description">
                                                                            <ValueWithSkeleton loading type="sentence" width="22ch" />
                                                                        </span>
                                                                        <span className="usa-checkbox__label-description">
                                                                            <ValueWithSkeleton loading type="sentence" width="28ch" />
                                                                        </span>
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </fieldset>
                                                    </div>
                                                ) : requestsError ? (
                                                    <div className="usa-alert usa-alert--error" role="alert">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{requestsError}</p>
                                                        </div>
                                                    </div>
                                                ) : requests.length === 0 ? (
                                                    <p>No access requests have been submitted yet.</p>
                                                ) : (
                                                    <div>
                                                        <div className="margin-bottom-2">
                                                            <fieldset className="usa-segmented-switch" aria-label="Filter request list">
                                                                <input
                                                                    className="usa-segmented-switch__input"
                                                                    type="radio"
                                                                    id="supervised-request-filter-open"
                                                                    name="supervised-request-filter"
                                                                    checked={requestFilter === REQUEST_FILTER_OPTIONS.OPEN}
                                                                    onChange={() => setRequestFilter(REQUEST_FILTER_OPTIONS.OPEN)}
                                                                />
                                                                <label
                                                                    className="usa-segmented-switch__label"
                                                                    htmlFor="supervised-request-filter-open"
                                                                >
                                                                    Open ({requestCounts.open})
                                                                </label>

                                                                <input
                                                                    className="usa-segmented-switch__input"
                                                                    type="radio"
                                                                    id="supervised-request-filter-closed"
                                                                    name="supervised-request-filter"
                                                                    checked={requestFilter === REQUEST_FILTER_OPTIONS.CLOSED}
                                                                    onChange={() => setRequestFilter(REQUEST_FILTER_OPTIONS.CLOSED)}
                                                                />
                                                                <label
                                                                    className="usa-segmented-switch__label"
                                                                    htmlFor="supervised-request-filter-closed"
                                                                >
                                                                    Closed ({requestCounts.closed})
                                                                </label>

                                                                <input
                                                                    className="usa-segmented-switch__input"
                                                                    type="radio"
                                                                    id="supervised-request-filter-all"
                                                                    name="supervised-request-filter"
                                                                    checked={requestFilter === REQUEST_FILTER_OPTIONS.ALL}
                                                                    onChange={() => setRequestFilter(REQUEST_FILTER_OPTIONS.ALL)}
                                                                />
                                                                <label
                                                                    className="usa-segmented-switch__label"
                                                                    htmlFor="supervised-request-filter-all"
                                                                >
                                                                    All ({requestCounts.all})
                                                                </label>
                                                            </fieldset>
                                                        </div>
                                                        {filteredRequests.length === 0 ? (
                                                            <p>{emptyRequestFilterMessage}</p>
                                                        ) : (
                                                            <div className="usa-form">
                                                                <fieldset className="usa-fieldset">
                                                                    {filteredRequests.map((request, index) => {
                                                                        const requestId = getRequestId(request);
                                                                        const displayRequestId = getDisplayRequestId(request);
                                                                        const inputId =
                                                                            requestId && requestId !== 'Unknown'
                                                                                ? requestId
                                                                                : `request-${index + 1}`;
                                                                        const idSuffix = normalizeIdentifier(inputId).replace(/[^A-Za-z0-9_-]/g, '-');
                                                                        const inputIdentifier = idSuffix || `request-${index + 1}`;
                                                                        const hasDisplayRequestId =
                                                                            displayRequestId && displayRequestId !== 'Unknown';
                                                                        return (
                                                                            <div key={`${inputIdentifier}-${index}`} className="usa-checkbox">
                                                                                <input
                                                                                    className="usa-checkbox__input usa-checkbox__input--tile"
                                                                                    id={`supervised-request-${inputIdentifier}`}
                                                                                    type="checkbox"
                                                                                    name="supervised-access-requests"
                                                                                    disabled
                                                                                />
                                                                                <label
                                                                                    className="usa-checkbox__label"
                                                                                    htmlFor={`supervised-request-${inputIdentifier}`}
                                                                                >
                                                                                    {hasDisplayRequestId ? (
                                                                                        <Link to={`/requests/${displayRequestId}`}>
                                                                                            {displayRequestId}
                                                                                        </Link>
                                                                                    ) : (
                                                                                        <ValueWithSkeleton value="" fallback="Unknown" />
                                                                                    )}
                                                                                    <span className="usa-checkbox__label-description">
                                                                                        <ValueWithSkeleton
                                                                                            value={getRequestEntitlementName(request)}
                                                                                            loading={isLoadingRequestLookups}
                                                                                            type="sentence"
                                                                                            fallback="Unknown entitlement"
                                                                                        />{' '}
                                                                                        ·{' '}
                                                                                        {getRequestEntitlementType(request)}
                                                                                    </span>
                                                                                    <span className="usa-checkbox__label-description">
                                                                                        Supervisor:{' '}
                                                                                        <ValueWithSkeleton
                                                                                            value={getRequestSupervisorName(request)}
                                                                                            loading={isLoadingRequestLookups}
                                                                                            type="text"
                                                                                            width="12ch"
                                                                                            fallback="Unknown user"
                                                                                        />{' '}
                                                                                        · Entitlement owner:{' '}
                                                                                        <ValueWithSkeleton
                                                                                            value={getRequestOwnerName(request)}
                                                                                            loading={isLoadingRequestLookups}
                                                                                            type="text"
                                                                                            width="12ch"
                                                                                            fallback="Unknown group"
                                                                                        />
                                                                                    </span>
                                                                                    <span className="usa-checkbox__label-description">
                                                                                        Status: {getRequestStatusLabel(request)}
                                                                                    </span>
                                                                                </label>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </fieldset>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </>
            ) : null}
        </AppLayout>
    );
};

export default SupervisedUserPage;
